import React, { useState } from 'react'
import {
  ClipboardList,
  Tags,
  Package,
  FileText,
  Hash,
  CheckCircle,
  ToggleLeft,
  ToggleRight,
  Sparkles,
} from 'lucide-react'
import { TenantAiPanel } from '../TenantAiPanel'
import { Spinner, NotAvailable } from '../ui'

// Owner-only "Studio setup": what the tenant has configured (questionnaires, keywords,
// products) plus the AI analysis module. Not part of the support-console handoff, which
// is staff-facing; kept here so owners don't lose what the old detail page showed.
const SECTIONS = [
  { key: 'questionnaires', label: 'Questionários', icon: ClipboardList },
  { key: 'keywords', label: 'Palavras-chave', icon: Tags },
  { key: 'products', label: 'Produtos', icon: Package },
  { key: 'ai', label: 'Análise por IA', icon: Sparkles },
]

export const ConfigTab = ({ tenantId, state, loading }) => {
  const [section, setSection] = useState('questionnaires')

  return (
    <section className="card flex flex-col gap-4 p-4">
      <div className="flex flex-wrap gap-1 self-start rounded-lg border border-admin-200 p-1">
        {SECTIONS.map((s) => {
          const Icon = s.icon
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setSection(s.key)}
              aria-pressed={section === s.key}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] ${
                section === s.key ? 'bg-admin-50 text-primary-800' : 'text-admin-900 hover:bg-admin-50'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {s.label}
            </button>
          )
        })}
      </div>

      {section === 'ai' ? (
        <TenantAiPanel tenantId={tenantId} />
      ) : loading && !state ? (
        <Spinner />
      ) : !state ? (
        <NotAvailable>Não foi possível carregar a configuração do estúdio.</NotAvailable>
      ) : (
        <>
        {/* Questionnaires */}
        {section === 'questionnaires' && (
          <div className="space-y-3">
            {state.questionnaires?.length === 0 ? (
              <div className="text-center py-8">
                <ClipboardList className="h-8 w-8 text-admin-300 mx-auto mb-2" />
                <p className="text-admin-500 text-sm">Nenhum questionário configurado</p>
              </div>
            ) : (
              state.questionnaires?.map((q) => (
                <div key={q.id} className="border border-admin-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-admin-500" />
                      <span className="font-medium text-admin-900">{q.name}</span>
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      q.is_active
                        ? 'bg-green-100 text-green-700'
                        : 'bg-admin-100 text-admin-500'
                    }`}>
                      {q.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>
                  {q.description && (
                    <p className="text-sm text-admin-600 mb-2">{q.description}</p>
                  )}
                  <div className="flex items-center gap-4 text-xs text-admin-500">
                    <span>{q.questions?.length || 0} perguntas</span>
                    {q.created_at && (
                      <span>Criado em {new Date(q.created_at).toLocaleDateString('pt-BR')}</span>
                    )}
                  </div>
                  {q.questions?.length > 0 && (
                    <div className="mt-3 border-t border-admin-100 pt-3">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-admin-500">
                            <th className="pb-1 font-medium">#</th>
                            <th className="pb-1 font-medium">Pergunta</th>
                            <th className="pb-1 font-medium">Tipo</th>
                            <th className="pb-1 font-medium">Obrigatória</th>
                          </tr>
                        </thead>
                        <tbody>
                          {q.questions.map((question, idx) => (
                            <tr key={question.id} className="border-t border-admin-50">
                              <td className="py-1.5 text-admin-400">{idx + 1}</td>
                              <td className="py-1.5 text-admin-800">{question.text}</td>
                              <td className="py-1.5">
                                <span className="inline-flex px-1.5 py-0.5 rounded bg-admin-100 text-admin-600 text-xs">
                                  {question.type}
                                </span>
                              </td>
                              <td className="py-1.5">
                                {question.is_required ? (
                                  <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                                ) : (
                                  <span className="text-admin-300">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Keyword Columns */}
        {section === 'keywords' && (
          <div className="space-y-3">
            {state.keyword_columns?.length === 0 ? (
              <div className="text-center py-8">
                <Tags className="h-8 w-8 text-admin-300 mx-auto mb-2" />
                <p className="text-admin-500 text-sm">Nenhuma coluna de palavras-chave configurada</p>
              </div>
            ) : (
              state.keyword_columns?.map((col) => (
                <div key={col.id} className="border border-admin-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Hash className="h-4 w-4 text-admin-500" />
                      <span className="font-medium text-admin-900">{col.title}</span>
                      <span className="text-xs text-admin-400">ordem: {col.column_order}</span>
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      col.is_active
                        ? 'bg-green-100 text-green-700'
                        : 'bg-admin-100 text-admin-500'
                    }`}>
                      {col.is_active ? 'Ativa' : 'Inativa'}
                    </span>
                  </div>
                  {col.description && (
                    <p className="text-sm text-admin-600 mb-2">{col.description}</p>
                  )}
                  {col.keywords?.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {col.keywords.map((kw) => (
                        <span
                          key={kw.id}
                          className="inline-flex items-center px-2 py-1 rounded-md bg-primary-50 text-primary-700 text-xs font-medium border border-primary-100"
                        >
                          {kw.text_english}
                          {kw.text_spanish && (
                            <span className="ml-1 text-primary-400">/ {kw.text_spanish}</span>
                          )}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-admin-400 mt-1">Nenhuma palavra-chave nesta coluna</p>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Products */}
        {section === 'products' && (
          <div className="space-y-3">
            {state.products?.length === 0 ? (
              <div className="text-center py-8">
                <Package className="h-8 w-8 text-admin-300 mx-auto mb-2" />
                <p className="text-admin-500 text-sm">Nenhum produto configurado</p>
              </div>
            ) : (
              state.products?.map((product) => (
                <div key={product.id} className="border border-admin-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-admin-500" />
                      <span className="font-medium text-admin-900">{product.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {product.price != null && (
                        <span className="text-sm font-medium text-admin-700">
                          {product.currency || 'BRL'} {product.price?.toFixed(2)}
                        </span>
                      )}
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        product.is_active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-admin-100 text-admin-500'
                      }`}>
                        {product.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                  </div>
                  {product.description && (
                    <p className="text-sm text-admin-600 mb-2">{product.description}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-3 text-xs text-admin-500 mt-2">
                    {product.display_order != null && (
                      <span>Ordem: {product.display_order}</span>
                    )}
                    <span className="flex items-center gap-1">
                      {product.list_in_explore ? (
                        <><ToggleRight className="h-3.5 w-3.5 text-green-500" /> Listado</>
                      ) : (
                        <><ToggleLeft className="h-3.5 w-3.5 text-admin-400" /> Não listado</>
                      )}
                    </span>
                    {product.allow_links && <span>Links permitidos</span>}
                    {product.questionnaire_id && <span>Tem questionário</span>}
                  </div>
                  {product.workflow_steps?.length > 0 && (
                    <div className="mt-3 border-t border-admin-100 pt-2">
                      <p className="text-xs font-medium text-admin-600 mb-1">Etapas do fluxo</p>
                      <div className="flex flex-wrap gap-1">
                        {product.workflow_steps.map((step, i) => {
                          const label =
                            typeof step === 'string'
                              ? step
                              : step?.title || step?.type || 'Etapa'
                          return (
                            <span
                              key={i}
                              className="inline-flex px-1.5 py-0.5 rounded bg-admin-100 text-admin-600 text-xs"
                              title={typeof step === 'object' ? step?.description : undefined}
                            >
                              {label}
                            </span>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        </>
      )}
    </section>
  )
}
